/*
 * Browser notifications.
 *
 * The shop has customers who have never installed the app and never will —
 * desktop, mostly — and between visits the only way to reach them is email.
 * This is the other way.
 *
 * The service worker is served from /apps/tru/sw.js rather than from the
 * theme, because a worker has to be same-origin with the pages that register
 * it and theme assets live on cdn.shopify.com. Shopify's app proxy forwards
 * that path to our API, which makes it same-origin.
 *
 * Its scope is only /apps/tru/, and that is fine: scope decides which pages a
 * worker *controls*, and push does not go through that. The subscription hangs
 * off the registration, so a worker registered from anywhere on the shop
 * receives notifications with the site shut.
 */
(function () {
  'use strict';

  var PROXY = '/apps/tru';
  var ASKED_KEY = 'tc_push_asked';

  // Everything here needs all three. Safari on iOS has the APIs but only
  // delivers to sites added to the Home Screen, which is nobody — those
  // customers have the actual app.
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return;
  }

  /** The VAPID public key arrives base64url; the browser wants bytes. */
  function keyBytes(base64url) {
    var padded = (base64url + '==='.slice((base64url.length + 3) % 4))
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    var raw = window.atob(padded);
    var bytes = new Uint8Array(raw.length);
    for (var i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
    return bytes;
  }

  function marketQuery() {
    var country = (window.Shopify && Shopify.country) || '';
    var lang = (document.documentElement.lang || '').slice(0, 2);
    return '?country=' + encodeURIComponent(country) + '&lang=' + encodeURIComponent(lang);
  }

  function post(path, body) {
    return fetch(PROXY + path + marketQuery(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  var ready = null;

  /** Register the worker once, and hand the registration to whoever asks. */
  function worker() {
    if (!ready) {
      ready = navigator.serviceWorker.register(PROXY + '/sw.js', { scope: PROXY + '/' });
    }
    return ready;
  }

  /** The server's public key, and whether this is switched on at all. */
  function settings() {
    return fetch(PROXY + '/webpush/key')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /**
   * Subscribe, assuming permission is already granted.
   *
   * Re-sent on every load rather than only on the first: the row carries the
   * market, the language and — when Shopify's proxy signature says so — who
   * the customer is, and all three change without the subscription changing.
   */
  function subscribe(force) {
    return settings().then(function (s) {
      if (!s || !s.enabled || !s.publicKey) return null;
      return worker().then(function (reg) {
        return reg.pushManager.getSubscription().then(function (existing) {
          if (existing) return existing;
          if (!force) return null;
          return reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: keyBytes(s.publicKey),
          });
        });
      });
    }).then(function (sub) {
      if (!sub) return null;
      return post('/webpush/subscribe', { subscription: sub }).then(function () { return sub; });
    });
  }

  /**
   * Ask.
   *
   * Never on landing: a permission prompt that arrives before anybody knows
   * what the shop is gets refused, and a refusal is close to permanent —
   * browsers will not ask again, and most customers never find the setting.
   * The caller decides the moment; see `TruPush.offer`.
   */
  function ask() {
    if (Notification.permission === 'denied') return Promise.resolve(false);
    if (Notification.permission === 'granted') return subscribe(true).then(Boolean);

    try { localStorage.setItem(ASKED_KEY, '1'); } catch (e) { /* private window */ }
    return Notification.requestPermission().then(function (result) {
      if (result !== 'granted') return false;
      return subscribe(true).then(Boolean);
    });
  }

  function alreadyAsked() {
    try { return localStorage.getItem(ASKED_KEY) === '1'; } catch (e) { return false; }
  }


  /* ─────────────────────────── the asking ─────────────────────────── */

  /*
   * Our own card first, the browser's prompt second.
   *
   * A browser gives a site exactly one permission prompt. Refuse it and it is
   * gone — Chrome will not ask again, and almost nobody goes looking for the
   * setting to undo it. So the real prompt is never spent on somebody who has
   * not been told what they are agreeing to. This card is the telling, it can
   * be declined harmlessly, and only "Allow" reaches the browser's own dialog.
   */

  var SAID_NO_KEY = 'tc_push_declined';
  var COOL_OFF_DAYS = 60;

  function declinedRecently() {
    try {
      var at = Number(localStorage.getItem(SAID_NO_KEY) || 0);
      return at > 0 && Date.now() - at < COOL_OFF_DAYS * 86400000;
    } catch (e) { return false; }
  }

  function rememberNo() {
    try { localStorage.setItem(SAID_NO_KEY, String(Date.now())); } catch (e) {}
  }

  /* The prompt's words, in the page's language. The layout writes them into
     data-labels on a hidden element from the theme's locale files, which
     locales-sync.js fills from the table the apps and the admin panel share,
     so the wording is edited in one place for the site and both apps.
     English stays here as the floor. */
  var EN = {
    title: 'Can we let you know?',
    lead: 'Turn on notifications and we will tell you about:',
    item_order: 'Your order — paid, packed and on its way',
    item_sales: 'Sales and member-only deals',
    item_restock: 'A size you wanted, back in stock',
    item_price: 'Price drops on things you are watching',
    yes: 'Allow notifications',
    no: 'No thanks',
    foot: 'You can turn them off whenever you like.',
  };

  function words() {
    var L = {};
    try {
      var node = document.querySelector('[data-tc-push-words]');
      var raw = JSON.parse((node && node.getAttribute('data-labels')) || '{}');
      for (var k in raw) if (Object.prototype.hasOwnProperty.call(raw, k) && typeof raw[k] === 'string') {
        L[k] = raw[k].replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
      }
    } catch (e) { L = {}; }
    var t = {};
    for (var key in EN) {
      var v = L[key];
      t[key] = (typeof v === 'string' && v && v.indexOf('translation missing') !== 0) ? v : EN[key];
    }
    t.items = [t.item_order, t.item_sales, t.item_restock, t.item_price];
    return t;
  }

  function esc(x) {
    return String(x).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function card() {
    var t = words();
    for (var k in t) if (k !== 'items') t[k] = esc(t[k]);
    t.items = t.items.map(esc);
    var wrap = document.createElement('div');
    wrap.className = 'tc-push-ask';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-label', t.title);
    wrap.innerHTML =
      '<div class="tc-push-ask__card">' +
        '<button class="tc-push-ask__x" aria-label="' + t.no + '">&times;</button>' +
        '<strong class="tc-push-ask__title">' + t.title + '</strong>' +
        '<p class="tc-push-ask__lead">' + t.lead + '</p>' +
        '<ul class="tc-push-ask__list">' +
          t.items.map(function (i) { return '<li>' + i + '</li>'; }).join('') +
        '</ul>' +
        '<div class="tc-push-ask__row">' +
          '<button class="tc-push-ask__no">' + t.no + '</button>' +
          '<button class="tc-push-ask__yes">' + t.yes + '</button>' +
        '</div>' +
        '<p class="tc-push-ask__foot">' + t.foot + '</p>' +
      '</div>';
    return wrap;
  }

  function close(el) {
    el.classList.remove('is-in');
    setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 200);
  }

  /**
   * Show the card. Resolves true only if the browser ended up granting.
   *
   * Declining costs the customer nothing — the real prompt is never raised, so
   * they can be asked again in a couple of months, which a browser-level "no"
   * would have made impossible.
   */
  function prompt() {
    return new Promise(function (resolve) {
      var el = card();
      document.body.appendChild(el);
      requestAnimationFrame(function () { el.classList.add('is-in'); });

      function no() { rememberNo(); close(el); resolve(false); }
      el.querySelector('.tc-push-ask__no').addEventListener('click', no);
      el.querySelector('.tc-push-ask__x').addEventListener('click', no);
      // The backdrop declines too. A centred card over a dimmer looks like a
      // trap unless clicking away works, and this one is an offer.
      el.addEventListener('click', function (e) { if (e.target === el) no(); });
      document.addEventListener('keydown', function esc(e) {
        if (e.key === 'Escape') { document.removeEventListener('keydown', esc); no(); }
      });
      el.querySelector('.tc-push-ask__yes').addEventListener('click', function () {
        close(el);
        ask().then(resolve);
      });
    });
  }

  window.TruPush = {
    /** Has this browser already said yes? */
    granted: function () { return Notification.permission === 'granted'; },
    /** Have we spent our one prompt? */
    asked: function () { return alreadyAsked() || Notification.permission !== 'default'; },
    ask: ask,
    /**
     * Offer, but only where it makes sense: somebody who has asked to be told
     * about something specific — a size coming back, an order on its way — has
     * a reason to say yes that a first-time visitor does not.
     */
    offer: function () {
      if (this.asked() || declinedRecently()) return Promise.resolve(false);
      return prompt();
    },
    /** The card on its own, for a button that says "notify me". */
    prompt: prompt,
    unsubscribe: function () {
      return worker()
        .then(function (reg) { return reg.pushManager.getSubscription(); })
        .then(function (sub) {
          if (!sub) return false;
          var endpoint = sub.endpoint;
          return sub.unsubscribe().then(function () {
            return post('/webpush/unsubscribe', { endpoint: endpoint }).then(function () { return true; });
          });
        });
    },
  };

  // A browser that already agreed gets its row refreshed quietly on each
  // visit. No prompt, no interruption — this is bookkeeping.
  if (Notification.permission === 'granted') {
    subscribe(false).catch(function () { /* offline, or the push service is down */ });
  }

  /* ────────────────────────── when to offer ────────────────────────── */

  /*
   * Not on landing, and not to everybody.
   *
   * The prompt is spent once, so it goes to people who have already shown
   * they want something from the shop: a full basket, or a size they have
   * asked to be told about. A first-time visitor two seconds into the home
   * page has no reason to say yes, and their "no" is the one that lasts.
   *
   * Anything else that earns the right to ask can call TruPush.offer().
   */
  /** Is something else already asking for attention? */
  function anotherModalIsUp() {
    // A native dialog, or Klaviyo's own onsite form — the shop's welcome
    // offer arrives through that one, and two cards at once is one too many.
    if (document.querySelector('dialog[open]')) return true;
    var forms = document.querySelectorAll('.klaviyo-form, [class*="kl-private-reset"], [data-testid="POPUP"]');
    for (var i = 0; i < forms.length; i++) {
      var box = forms[i].getBoundingClientRect();
      if (box.width > 120 && box.height > 120) return true;
    }
    return false;
  }

  function maybeOffer() {
    if (window.TruPush.asked() || declinedRecently()) return;

    /*
     * Anywhere, once somebody has been here a while.
     *
     * This used to wait for the basket, on the reasoning that a browser gives
     * a site one permission prompt and it should not be spent on a stranger.
     * That argument is weaker than it looked: the card is a soft ask, and
     * declining it never reaches the browser, so the real prompt stays
     * protected however often the card is shown. What the basket rule
     * achieved was that hardly anybody saw it.
     *
     * Twenty seconds is long enough to be past the welcome offer and to mean
     * the visitor stayed, and short enough to still catch them.
     */
    var waited = 0;
    (function offerWhenClear() {
      setTimeout(function () {
        if (window.TruPush.asked() || declinedRecently()) return;
        // Stand aside for whatever else is on screen, but not for ever.
        if (anotherModalIsUp() && waited < 3) { waited += 1; offerWhenClear(); return; }
        window.TruPush.offer();
      }, waited === 0 ? 20000 : 10000);
    })();

    // A back-in-stock signup does not wait: they have just asked to be told
    // something, so asking how to tell them is the same conversation.
    document.addEventListener('tc:stock-request', function () {
      setTimeout(function () { window.TruPush.offer(); }, 900);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeOffer);
  } else {
    maybeOffer();
  }

})();
